import { supabase } from '@/integrations/supabase/client';
import { devLog, devWarn } from '@/lib/devPerfLog';
// CRITICAL: Use lightweight cache instead of freeworldBootstrapStore
// This prevents FreeWorld data from loading on /module/* routes
import { loadTimePeriodsLightweight, getCachedTimePeriodsLightweight } from '@/lib/timePeriodsCache';
import { 
  loadFinancialStatus as loadFromCentralStore,
  getCachedFinancialStatus,
  resetFetchGuard,
  FinancialStatusData
} from '@/stores/financialStatusStore';
import type { TimePeriod, PlayerFinancialStatus } from '@/types/timeProgression';

// Lazy import for FreeWorld-only operations (checkTaskCompletion)
// These are only called from /freeworld routes
const loadFreeworldStore = () => import('@/stores/freeworldBootstrapStore');

// Type for the RPC response
interface FinancialStatusRpcResponse {
  id: string;
  player_id: string;
  current_money: number;
  total_earned: number;
  total_spent: number;
  current_time_period_id: string | null;
  created_at: string;
  updated_at: string;
}

// Result type for advanceToNextPeriod
export interface AdvanceToNextPeriodResult {
  newPeriod: TimePeriod;
  previousPeriod: TimePeriod;
  financialStatus: PlayerFinancialStatus;
  ageAdvanced: number;
}

// Error types for time progression
export class TimeProgressionError extends Error {
  constructor(
    message: string,
    public readonly code: 'OUT_OF_SYNC' | 'NO_NEXT_PERIOD' | 'SESSION_INVALID' | 'DB_ERROR'
  ) {
    super(message);
    this.name = 'TimeProgressionError';
  }
}

export class TimeProgressionService {
  static async getCurrentTimePeriod(playerId: string, sessionToken?: string): Promise<TimePeriod | null> {
    try {
      // If we have session token, use the centralized store to get financial status
      let targetPeriodId: string | null = null;
      
      if (sessionToken) {
        // CRITICAL: First check cache to avoid duplicate RPC
        const cachedData = getCachedFinancialStatus();
        if (cachedData?.current_time_period_id) {
          devLog('TimeService', 'getCurrentTimePeriod: Using cached financial status');
          targetPeriodId = cachedData.current_time_period_id;
        } else {
          // Use centralized store - handles caching and dedupe
          // Use mount-type reason for cooldown eligibility
          const financialData = await loadFromCentralStore({
            playerId,
            sessionToken,
            reason: 'bootstrap-getCurrentTimePeriod'
          });

          if (financialData) {
            targetPeriodId = financialData.current_time_period_id;
          }
        }
      }

      // If no financial record exists, get the first period from cached list
      if (!targetPeriodId) {
        const timePeriods = await loadTimePeriodsLightweight({ reason: 'getCurrentTimePeriod.firstPeriod' });
        const firstPeriod = timePeriods.find(p => p.is_active);
        if (!firstPeriod) {
          throw new Error('No active time periods found');
        }
        targetPeriodId = firstPeriod.id;
      }

      // Get the actual period data from cached list
      const timePeriods = getCachedTimePeriodsLightweight() ?? await loadTimePeriodsLightweight({ reason: 'getCurrentTimePeriod.lookup' });
      const periodData = timePeriods.find(p => p.id === targetPeriodId);

      if (!periodData) {
        throw new Error(`Time period not found: ${targetPeriodId}`);
      }

      return periodData;
    } catch (error) {
      devLog('TimeService', 'Error loading current time period', error);
      return null;
    }
  }

  static async checkTaskCompletion(playerId: string, timePeriodId: string, sessionToken?: string): Promise<boolean> {
    try {
      // Use centralized store if session token available
      let playerCurrentPeriodId = timePeriodId;
      
      if (sessionToken) {
        // CRITICAL: Check cache first to avoid duplicate RPC
        const cachedData = getCachedFinancialStatus();
        if (cachedData?.current_time_period_id) {
          playerCurrentPeriodId = cachedData.current_time_period_id;
        } else {
          const financialData = await loadFromCentralStore({
            playerId,
            sessionToken,
            reason: 'checkTaskCompletion'
          });

          if (financialData?.current_time_period_id) {
            playerCurrentPeriodId = financialData.current_time_period_id;
          }
        }
      }
      
      // LAZY LOAD: FreeWorld store only for checkTaskCompletion (FreeWorld-only operation)
      const freeworldStore = await loadFreeworldStore();
      
      // Get all time tasks for the current time period (uses cached store)
      const allTimeTasks = await freeworldStore.loadTimeTasks({ 
        periodId: timePeriodId, 
        reason: 'checkTaskCompletion' 
      });

      // Get all completed time tasks for this player (uses cached store)
      const completedTaskIds = await freeworldStore.loadTaskCompletions({ 
        playerId, 
        periodId: playerCurrentPeriodId, 
        reason: 'checkTaskCompletion' 
      });

      const totalTimeTasks = allTimeTasks?.length || 0;
      const completedTimeTasksCount = completedTaskIds?.length || 0;

      // If there are time tasks, check if they're all completed
      if (totalTimeTasks > 0) {
        return completedTimeTasksCount >= totalTimeTasks;
      }

      // If no time tasks exist, check if user has answered enough regular questions
      const { data: questionResponses, error: responseError } = await (supabase as any)
        .from('player_freeworld_answers')
        .select('id')
        .eq('player_id', playerId);

      if (responseError) throw responseError;

      const responseCount = questionResponses?.length || 0;
      const requiredResponses = 15;
      
      return responseCount >= requiredResponses;
    } catch (error) {
      devLog('TimeService', 'Error checking task completion', error);
      return false;
    }
  }

  static async getNextTimePeriod(currentPeriodId: string): Promise<TimePeriod | null> {
    try {
      // Get all periods from cache
      const timePeriods = getCachedTimePeriodsLightweight() ?? await loadTimePeriodsLightweight({ reason: 'getNextTimePeriod' });
      
      // Find current period
      const currentPeriod = timePeriods.find(p => p.id === currentPeriodId);
      if (!currentPeriod) {
        devLog('TimeService', 'Current period not found', { currentPeriodId });
        return null;
      }

      // Find next period by order
      const nextPeriod = timePeriods.find(
        p => p.is_active && p.period_order === currentPeriod.period_order + 1
      );

      return nextPeriod || null;
    } catch (error) {
      devLog('TimeService', 'Error getting next time period', error);
      return null;
    }
  }

  // DEPRECATED: Local cache removed - now using centralized financialStatusStore
  // Keeping reference for backward compatibility but it's no longer used
  private static financialStatusCache: Map<string, { data: PlayerFinancialStatus; timestamp: number }> = new Map();

  /**
   * Get or create financial status using centralized store
   * This method now delegates to the centralized financialStatusStore
   */
  static async getOrCreateFinancialStatus(
    playerId: string,
    periodData: TimePeriod | null,
    sessionToken?: string
  ): Promise<PlayerFinancialStatus | null> {
    // CRITICAL: Require session token to avoid 401 errors
    if (!sessionToken) {
      devWarn('TimeService', 'getOrCreateFinancialStatus: No session token, skipping');
      return null;
    }

    // CRITICAL: Check cache first to avoid duplicate RPC
    const cachedData = getCachedFinancialStatus();
    if (cachedData) {
      devLog('TimeService', 'getOrCreateFinancialStatus: Using cached data', {
        current_money: cachedData.current_money
      });
      
      return {
        id: '',
        player_id: playerId,
        current_money: cachedData.current_money,
        total_earned: cachedData.total_earned,
        total_spent: cachedData.total_spent,
        current_time_period_id: cachedData.current_time_period_id
      };
    }

    devLog('TimeService', 'getOrCreateFinancialStatus: Using centralized store', playerId.substring(0, 8) + '...');

    // Use centralized store - handles caching, dedupe, and fetch-once guard
    // Use mount-type reason for cooldown eligibility
    const financialData = await loadFromCentralStore({
      playerId,
      sessionToken,
      reason: 'bootstrap-getOrCreateFinancialStatus'
    });

    if (!financialData) {
      devWarn('TimeService', 'getOrCreateFinancialStatus: No data from centralized store');
      return null;
    }

    // Convert to PlayerFinancialStatus type
    // The centralized store returns a subset of fields; add placeholders for id/player_id
    // These fields are not typically used by callers that go through this method
    const result: PlayerFinancialStatus = {
      id: '', // Not available from centralized store
      player_id: playerId,
      current_money: financialData.current_money,
      total_earned: financialData.total_earned,
      total_spent: financialData.total_spent,
      current_time_period_id: financialData.current_time_period_id
    };

    devLog('TimeService', 'getOrCreateFinancialStatus: Loaded from centralized store', {
      current_money: result.current_money,
      total_earned: result.total_earned
    });

    return result;
  }

  // Method to invalidate cache when financial status is updated
  // Now delegates to centralized store
  static invalidateFinancialStatusCache(playerId?: string) {
    // Clear local cache (deprecated)
    this.financialStatusCache.clear();
    // Reset centralized store fetch guard
    resetFetchGuard(playerId);
    devLog('TimeService', 'invalidateFinancialStatusCache: Reset centralized store guard', { playerId });
  }

  static async updateFinancialStatus(
    playerId: string,
    updates: Partial<PlayerFinancialStatus>,
    sessionToken?: string
  ): Promise<PlayerFinancialStatus | null> {
    // CRITICAL: Require session token to avoid 401 errors
    if (!sessionToken) {
      devWarn('TimeService', 'updateFinancialStatus: No session token, skipping');
      return null;
    }

    devLog('TimeService', 'updateFinancialStatus: Sending update', {
      playerId: playerId.substring(0, 8) + '...',
      current_money: updates.current_money,
      total_earned: updates.total_earned,
      total_spent: updates.total_spent
    });

    try {
      const payload = {
        p_session_token: sessionToken,
        p_player_id: playerId,
        p_current_money: updates.current_money ?? null,
        p_total_earned: updates.total_earned ?? null,
        p_total_spent: updates.total_spent ?? null
      };

      const { data, error } = await supabase.rpc(
        'update_financial_status_with_session',
        payload
      );

      if (error) {
        devLog('TimeService', 'updateFinancialStatus RPC error', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return null;
      }

      // Invalidate cache after successful update
      this.invalidateFinancialStatusCache(playerId);

      devLog('TimeService', 'updateFinancialStatus: DB returned', data);

      if (data && typeof data === 'object') {
        return data as unknown as PlayerFinancialStatus;
      }
      
      devWarn('TimeService', 'updateFinancialStatus: RPC returned empty data');
      return null;
    } catch (error) {
      devLog('TimeService', 'Exception in updateFinancialStatus', error);
      return null;
    }
  }

  static async updatePlayerTimePeriod(
    playerId: string, 
    timePeriodId: string,
    sessionToken?: string
  ): Promise<void> {
    if (!sessionToken) {
      throw new Error('Session token is required to update player time period');
    }

    const { data, error } = await supabase.rpc(
      'update_player_time_period_with_session',
      {
        p_session_token: sessionToken,
        p_player_id: playerId,
        p_time_period_id: timePeriodId
      }
    );

    if (error) {
      devLog('TimeService', 'Failed to update player time period', error);
      throw error;
    }
    
    // Invalidate cache after time period change
    this.invalidateFinancialStatusCache(playerId);
    
    devLog('TimeService', 'Player time period updated', data);
  }

  /**
   * CENTRALIZED TIME ADVANCEMENT FUNCTION
   * 
   * This is the ONLY function that should update current_time_period_id and player age.
   * All UI components and hooks must route through this function.
   * 
   * Features:
   * - Idempotency guard: if expectedCurrentPeriodId doesn't match DB, abort
   * - Centralized age advancement
   * - Cache invalidation
   * - Event dispatch for listeners
   */
  static async advanceToNextPeriod(options: {
    playerId: string;
    sessionToken: string;
    expectedCurrentPeriodId?: string | null;
  }): Promise<AdvanceToNextPeriodResult> {
    const { playerId, sessionToken, expectedCurrentPeriodId } = options;

    devLog('TimeService', 'advanceToNextPeriod called', {
      playerId: playerId.substring(0, 8) + '...',
      expectedCurrentPeriodId
    });

    // 1. Get current financial status from DB (fresh, via centralized store)
    // Use force=true since this is an event-type call that needs fresh data
    const financialData = await loadFromCentralStore({
      playerId,
      sessionToken,
      reason: 'advance_to_next_period',
      force: true
    });

    if (!financialData) {
      devLog('TimeService', 'advanceToNextPeriod: Failed to get financial status');
      throw new TimeProgressionError('Failed to get financial status', 'DB_ERROR');
    }

    const currentFinancialStatus: PlayerFinancialStatus = {
      id: '',
      player_id: playerId,
      current_money: financialData.current_money,
      total_earned: financialData.total_earned,
      total_spent: financialData.total_spent,
      current_time_period_id: financialData.current_time_period_id
    };
    const dbCurrentPeriodId = currentFinancialStatus?.current_time_period_id;

    // 2. IDEMPOTENCY GUARD: Check if we're in sync
    if (expectedCurrentPeriodId !== undefined && expectedCurrentPeriodId !== null) {
      if (dbCurrentPeriodId !== expectedCurrentPeriodId) {
        devWarn('TimeService', 'advanceToNextPeriod: OUT OF SYNC - aborting', {
          expected: expectedCurrentPeriodId,
          actual: dbCurrentPeriodId
        });
        throw new TimeProgressionError(
          'Time progression out of sync - period already advanced or changed',
          'OUT_OF_SYNC'
        );
      }
    }

    // 3. Get current period data from cache
    if (!dbCurrentPeriodId) {
      throw new TimeProgressionError('Player has no current time period', 'DB_ERROR');
    }

    const timePeriods = getCachedTimePeriodsLightweight() ?? await loadTimePeriodsLightweight({ reason: 'advanceToNextPeriod.currentPeriod' });
    const currentPeriodData = timePeriods.find(p => p.id === dbCurrentPeriodId);

    if (!currentPeriodData) {
      throw new TimeProgressionError('Failed to get current period data', 'DB_ERROR');
    }

    const currentPeriod = currentPeriodData as TimePeriod;

    // 4. Get next period
    const nextPeriod = await this.getNextTimePeriod(dbCurrentPeriodId);
    
    if (!nextPeriod) {
      devLog('TimeService', 'advanceToNextPeriod: No next period - game completed');
      throw new TimeProgressionError('No next time period available - simulation complete', 'NO_NEXT_PERIOD');
    }

    devLog('TimeService', 'advanceToNextPeriod: Advancing', {
      from: currentPeriod.period_name,
      to: nextPeriod.period_name
    });

    // 5. Update player's time period
    await this.updatePlayerTimePeriod(playerId, nextPeriod.id, sessionToken);

    // 6. Advance character age (centralized, single location)
    const ageIncrease = nextPeriod.jump_duration_value || 1;
    await this.advanceCharacterAge(playerId, sessionToken, ageIncrease);

    // 7. Get updated financial status
    const updatedFinancialStatus = await this.getOrCreateFinancialStatus(
      playerId,
      nextPeriod,
      sessionToken
    );

    if (!updatedFinancialStatus) {
      throw new TimeProgressionError('Failed to get updated financial status', 'DB_ERROR');
    }

    // 8. Dispatch event for all listeners
    devLog('TimeService', 'Dispatching timePeriodChanged event');
    window.dispatchEvent(new CustomEvent('timePeriodChanged', {
      detail: {
        previousPeriod: currentPeriod,
        newPeriod: nextPeriod,
        playerId
      }
    }));

    return {
      newPeriod: nextPeriod,
      previousPeriod: currentPeriod,
      financialStatus: updatedFinancialStatus,
      ageAdvanced: ageIncrease
    };
  }

  /**
   * Centralized age advancement - called only from advanceToNextPeriod
   */
  private static async advanceCharacterAge(
    playerId: string,
    sessionToken: string,
    years: number
  ): Promise<void> {
    try {
      // PERF: Use SessionTokenManager for deduplicated token setting
      const { default: SessionTokenManager } = await import('@/services/sessionTokenManager');
      await SessionTokenManager.ensureTokenSet(sessionToken);

      // Get current age
      const { data: profile, error: fetchError } = await supabase
        .from('character_profiles')
        .select('age')
        .eq('player_id', playerId)
        .maybeSingle();

      if (fetchError) {
        devWarn('TimeService', 'Failed to get character profile for age update', fetchError);
        return;
      }

      if (!profile) {
        devWarn('TimeService', 'No character profile found for age update');
        return;
      }

      const newAge = profile.age + years;

      // Update age
      const { error: updateError } = await supabase
        .from('character_profiles')
        .update({ age: newAge })
        .eq('player_id', playerId);

      if (updateError) {
        devWarn('TimeService', 'Failed to update character age', updateError);
        return;
      }

      devLog('TimeService', 'Character age advanced', { from: profile.age, to: newAge });
    } catch (error) {
      devWarn('TimeService', 'Exception in advanceCharacterAge', error);
    }
  }
}